import React, { FC, useState } from 'react'

import clsx from 'clsx'
import Select, { components, MultiValueRemoveProps, GroupBase, MultiValueProps } from 'react-select'
import { cloneDeep } from 'lodash'

import { getFloatPrice } from '../../../../../helpers/price'
import { sortTeamMembersByRegistrar } from '../../../../../helpers/registration-on-event'

import { IEventFees } from '../../../../../models/event-fees/event-fees.interface'
import { IHandleTeamMateChange } from '../ClinicNOtherRegisterTab'
import { IRegisterTab } from '../../../../../models/event-registered-users/event-registered-users.interface'

import { registerCustomStyles } from '../../../../../components/customStyles/ReactSelectCustomStyle'

import { MODAL_CONSTS } from '../../../../../const/modal-const'
import { daysOfWeek } from '../../../../../helpers/time'
import { useAppSelector } from '../../../../../store/hooks'
import {
  selectRegistration,
  selectTeamTabData,
} from '../../../../../store/registration/registrationSlice'

const CustomMultiValueRemove = (props: MultiValueRemoveProps<any, true, GroupBase<any>>) => {
  const { data } = props
  if (data.divisionUniqId) return null
  return <components.MultiValueRemove {...props} />
}

const CustomMultiValue = (props: MultiValueProps<any, true, GroupBase<any>>) => {
  const { data } = props
  const customStyle = data.divisionUniqId ? { opacity: 0.5 } : {}

  return (
    <div style={{ ...registerCustomStyles.multiValue, ...customStyle }}>
      <components.MultiValue {...props} />
    </div>
  )
}

interface ItemProps {
  row: IRegisterTab
  handleTeamMateChange: ({ id, currentClass }: IHandleTeamMateChange) => Promise<void | null>
  handleModal: (showHide: boolean, typeOfModal: string, dataToPassOn?: any) => void
  eventId: string
  activeDay: string
  EventFees: IEventFees | null
  currency: string
}

export const RegisterItem: FC<ItemProps> = ({
  row,
  handleTeamMateChange,
  handleModal,
  eventId,
  activeDay,
  EventFees,
  currency,
}) => {
  const [loading, setLoading] = useState(false)

  const teamTabData = useAppSelector(selectTeamTabData)
  const registration = useAppSelector(selectRegistration)

  let currentClass = row

  const isStartDate = daysOfWeek[new Date(row.startDate)?.getDay()] === activeDay
  const isStartDateChildren = row.children?.find(
    (children) => daysOfWeek[new Date(children.startDate)?.getDay()] === activeDay
  )
  if (isStartDate || isStartDateChildren) {
    return (
      <div
        className={clsx(
          `w-full flex justify-between flex-wrap lg:mb-0 gap-2 text-SeabiscuitDark200ThemeColor`,
          row.children?.[0] && 'border border-SeabiscuitLightThemeColorD3 rounded-md p-4'
        )}
      >
        <div
          className={clsx(
            'w-full md:w-auto flex-1 gap-2 text-base flex flex-wrap items-center  pr-4',
            !row.children?.[0] && 'py-3 pl-4 border rounded-lg border-SeabiscuitLightThemeColorD3'
          )}
        >
          {row.registrationByDayName}
          {row.order ? (
            <span className="text-[14px] text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-0.5 px-3">
              {row.order}
            </span>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center justify-end text-SeabiscuitDark200ThemeColor/50">
            {!row.children?.[0] && (
              <>
                <span className="text-SeabiscuitDark200ThemeColor mr-1">
                  {getFloatPrice(`${currency}${row.price}`)}
                </span>
                entry fee •
                <span className="text-SeabiscuitDark200ThemeColor mx-1">
                  {row.qualifyFee && EventFees?.qualifyingClasses?.isEnable && row.qualifyFeeName
                    ? `${currency}${row.qualifyFee}`
                    : ''}
                </span>
                {row.qualifyFeeName || 'No extra fee'} •
              </>
            )}
            <button
              className="ml-1 underline hover:no-underline"
              onClick={() => handleModal(true, MODAL_CONSTS.CLASS_MORE, { row, eventId })}
            >
              More
            </button>
          </div>
        </div>
        <div
          className={clsx(
            'w-full md:w-[20%] flex items-center border border-SeabiscuitLightThemeColorD3 min-w-[180px] text-SeabiscuitDark200ThemeColor text-sm rounded-lg relative'
          )}
        >
          <Select
            isMulti
            menuPlacement="auto"
            menuPortalTarget={document.body}
            components={{ MultiValueRemove: CustomMultiValueRemove, MultiValue: CustomMultiValue }}
            onChange={async (_currenValue, actionMeta: any) => {
              setLoading(true)
              await handleTeamMateChange({
                id: actionMeta.option?.value
                  ? actionMeta.option?.value.split('_')[0]
                  : actionMeta.removedValue?.value
                    ? actionMeta.removedValue?.value.split('_')[0]
                    : '',
                currentClass: cloneDeep(currentClass),
                removedUniqId: actionMeta.removedValue?.uniqId ?? null,
              })
              setLoading(false)
            }}
            value={currentClass.registrationsByDay?.map((currRegistrationByDay, index) => ({
              index,
              value: `${currRegistrationByDay?.riderId}_${index}`,
              label: currRegistrationByDay?.riderName || '',
              uniqId: currRegistrationByDay?.uniqId || '',
              divisionUniqId: currRegistrationByDay?.divisionUniqId || '',
            }))}
            isClearable={false}
            isSearchable={false}
            isLoading={loading}
            isDisabled={loading}
            styles={registerCustomStyles}
            options={
              sortTeamMembersByRegistrar(teamTabData, registration?.userId)?.map((curr, index) => ({
                index,
                label: curr.memberName ?? '',
                value: `${curr.memberId}_${new Date().getTime()}`,
              })) ?? []
            }
            placeholder="Select Riders"
            className="w-full rounded-lg searchableComponent focus:ring-0 p-0 focus:ring-transparent min-h-[52px] flex items-center"
          />
        </div>
        {row.children?.map((children, childrenIndex) => (
          <RegisterItem
            key={childrenIndex}
            row={children}
            handleTeamMateChange={handleTeamMateChange}
            handleModal={handleModal}
            eventId={eventId}
            activeDay={activeDay}
            EventFees={EventFees}
            currency={currency}
          />
        ))}
      </div>
    )
  } else {
    return null
  }
}
